/**
 * The berth feature (M8 plan MC-b4; MC-b6a adds the station panel and the access wiring): registers `berth.store` and
 * `berth.take` on the craft hook (`craft/hook.ts`), so `UiActions.craft` runs them, and, in the Reach, the Berth
 * station tab (`ui/berth/Berth.tsx`). Both commands act only on the berth of the station the player is docked at.
 *
 * - **Owner:** the berth's owner (stamped at the first deposit, `career/berth.ts store`) is the station's
 *   `StationView.owner` from the strategic client's cached system view; without a strategic link (M2 career mode),
 *   or before the view lists the station, it is the station's own faction.
 * - **Access (MC-b5, D-500):** both handlers pass `berthAccess` from that cached view into `store`/`take`, so a frozen
 *   (disabled station) or relocating berth refuses with its goods intact. Without a view the stamp alone decides.
 * - **Held for a fab order (MC-c8):** a berthed component named in an open order's `FabOrder.salvageIn` is refused by
 *   `take` (`serviceUnavailable`, `detail.reason: 'heldForFabOrder'`); the order consumes or returns it.
 * - **The station's view** is found by the Reach layout's site for the docked station (home-cluster stations keep
 *   M2's ids, `bridge/m2/stationTabs.ts siteOfStation`), else by strategic id or career slug.
 */
import { FEATURE_ORDER, type GameContext, type GameFeature } from '../../app/feature'
import { siteOfStation } from '../../bridge/m2/stationTabs'
import { m2IdOfSite, m2StationId } from '../../bridge/m2/stations'
import { store, take, type BerthData } from '../../career/berth'
import type { CommandError } from '../../career/result'
import { registerCraftHandler, type CraftCtx, type CraftHandler, type CraftCommandMap } from '../../craft/hook'
import type { StationDef, StationId } from '../../data/m2Schemas'
import type { SystemLayout } from '../../galaxy/layout/types'
import type { StationServiceRegistry } from '../../reach/ports'
import { createBerthPanel, type BerthPanelDeps } from '../../ui/berth/Berth'
import type { CmdOutcome } from '../../ui/contracts'
import { BERTH_UI, berthAccessAt, fabOrderHolding, type BerthModelInput } from '../../ui/models/berth'
import type { CorpFactionId, StationView } from '../../strategic/types'

/** After the career runtime (the registry is global, so the order only places it in the list). */
const ORDER = FEATURE_ORDER.career + 1

/** The owner a berth at `station` is stamped with. */
export type BerthOwnerOf = (station: StationDef) => CorpFactionId

/** The cached view of the berth station `stationId`, or null (no strategic link, or not listed yet). */
export type BerthViewOf = (stationId: StationId) => StationView | null

/**
 * The cached view's owner of `station` (by strategic id, or its M2 id through `m2IdOfSite` over `layout`: R3-1 sweep,
 * the home Hub's view is `sys-07-st0`), else the station's own faction.
 */
export function berthOwner(
  station: StationDef,
  views: readonly StationView[] | null | undefined,
  layout: Pick<SystemLayout, 'stations' | 'home'> | null = null,
): CorpFactionId {
  const v = views?.find((s) => s.id === station.id || m2IdOfSite(s.id, layout) === station.id)
  return v?.owner ?? station.faction
}

/**
 * The cached view of career station `stationId` among `views`: the one whose id is the Reach layout's site for it
 * (home-cluster stations keep M2's ids), else the one with that strategic id or career slug. Null when none.
 */
export function berthViewOf(
  stationId: StationId,
  views: readonly StationView[] | null | undefined,
  layout: Pick<SystemLayout, 'stations' | 'home'> | null,
): StationView | null {
  if (views === null || views === undefined) return null
  const site = siteOfStation(layout, stationId)
  return views.find((s) => (site !== null && s.id === site.id) || s.id === stationId || m2StationId(s.id) === stationId) ?? null
}

const NOT_DOCKED: CommandError = { code: 'serviceUnavailable', message: 'Dock at the station to use its berth.' }

/** The docked station when it is `stationId`, else the refusal. */
function dockedAt(ctx: CraftCtx, stationId: string): StationDef | CommandError {
  const here = ctx.station
  if (here === null || here.id !== stationId) return { ...NOT_DOCKED, detail: { station: stationId } }
  return here
}

function isStation(v: StationDef | CommandError): v is StationDef {
  return 'id' in v
}

/**
 * The two handlers (exported for the tests; the feature registers them). `viewOf` (default: none) gives the
 * station's cached view for the berth's access (D-500); the owner comes from `ownerOf`.
 */
export function createBerthHandlers(
  ownerOf: BerthOwnerOf,
  viewOf: BerthViewOf = () => null,
): {
  store: CraftHandler<CraftCommandMap['berth.store']>
  take: CraftHandler<CraftCommandMap['berth.take']>
} {
  return {
    store(cmd, ctx): CmdOutcome {
      const here = dockedAt(ctx, cmd.stationId)
      if (!isStation(here)) return ctx.refuse(here)
      const data: BerthData = ctx.data
      const career = ctx.career()
      const owner = ownerOf(here)
      const access = berthAccessAt(career, data, cmd.stationId, viewOf(cmd.stationId), owner, ctx.now)
      const r = store(career, data, cmd.stationId, cmd.sel, owner, access)
      if (!r.ok) return ctx.refuse(r.error)
      ctx.commit(r.value, r.effects)
      return ctx.done()
    },
    take(cmd, ctx): CmdOutcome {
      const here = dockedAt(ctx, cmd.stationId)
      if (!isStation(here)) return ctx.refuse(here)
      const career = ctx.career()
      for (const id of cmd.sel.components ?? []) {
        const order = fabOrderHolding(career, id)
        if (order !== null) return ctx.refuse({ code: 'serviceUnavailable', message: BERTH_UI.text.heldForFabRefused, detail: { id, order, reason: 'heldForFabOrder' } })
      }
      const access = berthAccessAt(career, ctx.data, cmd.stationId, viewOf(cmd.stationId), ownerOf(here), ctx.now)
      const r = take(career, ctx.data, cmd.stationId, cmd.sel, access)
      if (!r.ok) return ctx.refuse(r.error)
      ctx.commit(r.value, r.effects)
      return ctx.done()
    },
  }
}

const NO_BERTH: CmdOutcome = { ok: false, error: { code: 'serviceUnavailable', message: 'The berth is not available.' } }

/** The panel's reads and actions over the career runtime and the strategic client (both looked up lazily). */
export function berthPanelDeps(ctx: GameContext): BerthPanelDeps {
  const views = (): readonly StationView[] => ctx.services.maybe('strategic')?.systemView?.stations ?? []
  const layout = (): SystemLayout | null => ctx.reach?.session.layout ?? null
  return {
    input(site): BerthModelInput | null {
      const m2 = ctx.services.maybe('m2')
      if (m2 === undefined) return null
      const ui = m2.ui.getState()
      const career = ui.career
      if (career === null) return null
      const docked = ui.stationId !== null && siteOfStation(layout(), ui.stationId)?.id === site.id ? ui.stationId : null
      const stationId = docked ?? m2IdOfSite(site.id, layout())
      const stations = views()
      const view = berthViewOf(stationId, stations, layout())
      const def = m2.data.stations.stations.find((s) => s.id === stationId)
      const owner = view?.owner ?? def?.faction ?? site.faction
      return { data: m2.data, career, stationId, docked: docked !== null, view, stations, owner, now: ui.gameTime }
    },
    run(cmd): CmdOutcome {
      const craft = ctx.services.maybe('m2')?.ui.getState().actions.craft
      return craft === undefined ? NO_BERTH : craft(cmd)
    },
    subscribe(fn): () => void {
      const offs: (() => void)[] = []
      const m2 = ctx.services.maybe('m2')
      if (m2 !== undefined) {
        offs.push(
          m2.ui.subscribe((s, prev) => {
            if (s.career !== prev.career || s.stationId !== prev.stationId) fn()
          }),
        )
      }
      const client = ctx.services.maybe('strategic')
      if (client !== undefined) offs.push(client.onSystemView(() => fn()))
      return () => {
        for (const off of offs.splice(0)) off()
      }
    },
  }
}

/** Registries the panel already went into (the registry has no removal; a re-init must not register twice). */
const registered = new WeakSet<StationServiceRegistry>()

let unregister: (() => void)[] = []

const feature: GameFeature = {
  name: 'berth',
  order: ORDER,
  init(ctx: GameContext): void {
    // Read lazily: the strategic link provides its client after this feature's init.
    const views = (): readonly StationView[] | undefined => ctx.services.maybe('strategic')?.systemView?.stations
    const viewOf: BerthViewOf = (id) => berthViewOf(id, views(), ctx.reach?.session.layout ?? null)
    const handlers = createBerthHandlers((station) => viewOf(station.id)?.owner ?? station.faction, viewOf)
    unregister = [registerCraftHandler('berth.store', handlers.store), registerCraftHandler('berth.take', handlers.take)]
    const reach = ctx.reach
    if (reach !== null && reach !== undefined && !registered.has(reach.services)) {
      registered.add(reach.services)
      reach.services.register(createBerthPanel(berthPanelDeps(ctx)))
    }
  },
  dispose(): void {
    for (const off of unregister) off()
    unregister = []
  },
}

export default feature
